import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "../lib/gsap";

/**
 * Scroll-triggered reveal: fade + 40px rise, staggered for child elements
 * marked with [data-reveal]. Falls back to visible immediately when reduced
 * motion is requested.
 */
export function useReveal<T extends HTMLElement>() {
  const scope = useRef<T>(null);

  useEffect(() => {
    if (!scope.current) return;
    const targets = scope.current.querySelectorAll<HTMLElement>("[data-reveal]");
    if (!targets.length) return;

    if (prefersReducedMotion()) {
      gsap.set(targets, { opacity: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      // fromTo with an explicit visible end state + toggleActions, so content
      // reliably ends visible. If the section is already in view on load,
      // ScrollTrigger fires immediately; below-fold sections reveal on scroll.
      gsap.fromTo(
        targets,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: scope.current!,
            start: "top 85%",
            toggleActions: "play none none none",
            once: true,
          },
        }
      );
    }, scope);

    // Safety net: if ScrollTrigger never fires (init failure), never leave
    // content stuck invisible — force-reveal after a short delay.
    const safety = window.setTimeout(() => {
      targets.forEach((el) => {
        if (getComputedStyle(el).opacity === "0") gsap.set(el, { opacity: 1, y: 0 });
      });
    }, 2500);

    return () => {
      window.clearTimeout(safety);
      ctx.revert();
    };
  }, []);

  return scope;
}
